import React, { useEffect, useState, useCallback } from 'react';
import DistBars from '../components/DistBars';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from 'recharts';

// 🧪 Minőség — napi quality_reports auditjelentések
const card = { background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 12, padding: 16 };
const title = { fontSize: 13, color: '#8b8fa3', fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 };
const inp = { background: '#0a0d14', border: '1px solid #2a2e3a', borderRadius: 6, padding: '8px 10px', color: '#e4e6eb', fontSize: 13, boxSizing: 'border-box' };
const btn = { padding: '8px 16px', borderRadius: 6, border: 'none', background: '#00d4ff', color: '#0a0d14', cursor: 'pointer', fontWeight: 600, fontSize: 12 };

// Az ellenőrzések központi leírása. A CHECK_LABELS sorrendje a megjelenítési
//   sorrend is. Új ellenőrzésnél ezt a három map-et és a CRIT/WARN listát kell
//   bővíteni — a komponensekben nincs beégetett ellenőrzésnév.
const CHECK_LABELS = {
  scriptMixing: 'Írásrendszer-keveredés',
  eraMarking: 'Korszakjelölés (i. e. / i. sz.)',
  sceneAdherence: 'Jelenet-stílus betartása',
  openerEcho: 'Nyitás-ismétlés',
  entityMixup: 'Entitás-tévesztés',
  placeConsistency: 'Helykonzisztencia',
  foreignNameForm: 'Idegen névalak',
  unsourcedName: 'Forrás nélküli név',
  promptPathMismatch: 'Prompt-út ellentmondás',
};
// Rövid név az összesítő kártyák alcímébe.
const CHECK_SHORT = {
  scriptMixing: 'írásrendszer',
  eraMarking: 'korszakjelölés',
  sceneAdherence: 'jelenet',
  openerEcho: 'nyitás-ismétlés',
  entityMixup: 'entitás',
  placeConsistency: 'helykonzisztencia',
  foreignNameForm: 'idegen névalak',
  unsourcedName: 'forrás nélküli név',
  promptPathMismatch: 'prompt-út',
};
// Tooltip és a lenyitott ellenőrzés fejléce.
const CHECK_DESCRIPTIONS = {
  placeConsistency: 'A forrás (Wikipedia-cikk vagy ellenőrzött tények) más településhez tartozik, mint a POI. Nyelvfüggetlen.',
  foreignNameForm: 'A forrás-cikk címe a forrásnyelvi alakban került a narrációba, pedig van célnyelvi alak (pl. „Tours-i Szent Márton" német szövegben „Martin von Tours" helyett).',
  unsourcedName: 'Forrás nélküli (rövid prompt) narrációban a POI-hoz nem köthető tulajdonnév — kitalált személy/név gyanúja.',
  promptPathMismatch: 'Forrás megtalálva, mégis rövid prompt futott — kódhiba a promptépítőben.',
};
const CRIT_KEYS = ['scriptMixing', 'entityMixup', 'sceneAdherence', 'promptPathMismatch'];
const WARN_KEYS = ['eraMarking', 'openerEcho', 'placeConsistency', 'foreignNameForm', 'unsourcedName'];
const CHECK_ORDER = Object.keys(CHECK_LABELS);

// A riport ellenőrzései rögzített sorrendben: előbb az ismertek a CHECK_LABELS
//   sorrendjében, aztán az ismeretlenek (egy jövőbeli ellenőrzés se vesszen el).
//   Csak az szerepel, ami a dokumentumban tényleg megvan — a régi riportokban a
//   később bevezetett kulcsok hiányoznak, azokhoz nem rajzolunk üres sort.
const orderedChecks = (checks) => {
  if (!checks) return [];
  const known = CHECK_ORDER.filter((k) => checks[k]);
  const extra = Object.keys(checks).filter((k) => !CHECK_LABELS[k] && checks[k]);
  return [...known, ...extra].map((k) => [k, checks[k]]);
};
// A riport byPromptPath mezője: melyik prompt-úton futottak a mintában lévő narrációk.
const PROMPT_PATH_LABELS = { rich: 'gazdag', short: 'rövid', unknown: 'ismeretlen' };
const PROMPT_PATH_COLORS = { rich: '#22c55e', short: '#8b8fa3', unknown: '#5b6270' };
const TROPE_LABELS = { lightplay: 'fényjáték', radiates: 'árad/sugárzik', stop_and_watch: 'állj meg és figyeld' };
const SEV_COLOR = { error: '#f43f5e', warn: '#f59e0b' };

// A kártya alcíme: csak azok az ellenőrzések, amik ebben a riportban tényleg
//   lefutottak — egy régi riportnál ne soroljunk fel később bevezetett ellenőrzést.
const checkNames = (report, keys) =>
  keys.filter((k) => report?.checks?.[k]).map((k) => CHECK_SHORT[k]).join(' + ') || '—';

const sumChecks = (report, keys) =>
  keys.reduce((a, k) => a + (report?.checks?.[k]?.count || 0), 0);

// A kredit-egyeztetés NEM a két darabszámot veti össze, hanem requestId-párosítást
// végez: riasztás csak akkor van, ha marad árva tétel. Ezért a darabszámok lehetnek
// egyenlők (pl. 9 és 9) úgy is, hogy közben van 1-1 árva a két oldalon.
//   chargedWithoutTx → levonás történt, de nincs hozzá tranzakció-log (láthatatlan levonás)
//   txWithoutCharged → van tranzakció-log, de nincs hozzá levonás (dupla-log gyanú)
const orphanTotal = (rec) =>
  (rec?.chargedWithoutTx?.length || 0) + (rec?.txWithoutCharged?.length || 0);

function orphanSummary(rec) {
  const a = rec?.chargedWithoutTx?.length || 0;
  const b = rec?.txWithoutCharged?.length || 0;
  const parts = [];
  if (a) parts.push(`${a} levonás log nélkül`);
  if (b) parts.push(`${b} log levonás nélkül`);
  return parts.length ? 'árva tétel: ' + parts.join(' · ') : 'árva tétel';
}

function Card({ label, value, color, sub }) {
  return (
    <div style={{ ...card, minWidth: 160, flex: 1 }}>
      <div style={title}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || '#e4e6eb' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#8b8fa3', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function Quality() {
  const [date, setDate] = useState('');
  const [report, setReport] = useState(null);
  const [exists, setExists] = useState(true);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [trend, setTrend] = useState([]);
  const [openCheck, setOpenCheck] = useState(null);
  const [exporting, setExporting] = useState(false);

  const loadReport = useCallback(async (d) => {
    setLoading(true); setError(null);
    try {
      const q = d ? `?date=${encodeURIComponent(d)}` : '';
      const res = await fetch(`/api/quality/report${q}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setExists(!!j.exists);
      setReport(j.report || null);
      if (j.exists && j.report?.date && !d) setDate(j.report.date);
      if (!j.exists && j.date) setDate(j.date);
    } catch (e) { setError(e.message); setReport(null); }
    finally { setLoading(false); }
  }, []);

  const loadTrend = useCallback(async () => {
    try {
      const res = await fetch('/api/quality/trend?days=14', { credentials: 'include' });
      if (!res.ok) return;
      const j = await res.json();
      const pts = (j.items || []).map((r) => {
        const n = r.sampleCount || 0;
        const crit = CRIT_KEYS.reduce((a, k) => a + (r.checks?.[k]?.count || 0), 0);
        const trope = Object.values(r.tropes || {}).reduce((a, v) => a + (v || 0), 0);
        const over = r.lengthOver?.count || 0;
        const pct = (v) => (n ? Math.round((v / n) * 1000) / 10 : 0);
        return { date: (r.date || '').slice(5), kritikus: pct(crit), tropus: pct(trope), hossz: pct(over) };
      });
      setTrend(pts);
    } catch (_) { /* trend nem kritikus */ }
  }, []);

  useEffect(() => { loadReport(); loadTrend(); }, [loadReport, loadTrend]);

  const runAudit = async () => {
    if (!date) { setError('Válassz dátumot az audit futtatásához.'); return; }
    setRunning(true); setError(null);
    try {
      const res = await fetch('/api/quality/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ date }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      await loadReport(date);
      await loadTrend();
    } catch (e) { setError(e.message); }
    finally { setRunning(false); }
  };

  // Excel-jelentés a kiválasztott nap riportjából (+ a hozzá tartozó 14 napos trend).
  const exportExcel = async () => {
    setExporting(true); setError(null);
    try {
      const d = report?.date || date;
      const res = await fetch(`/api/quality/export${d ? `?date=${encodeURIComponent(d)}` : ''}`, { credentials: 'include' });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j.error) msg = j.error; } catch (_) {}
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `minoseg_${d || 'riport'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setExporting(false); }
  };

  const crit = sumChecks(report, CRIT_KEYS);
  const warns = sumChecks(report, WARN_KEYS);
  const rec = report?.creditReconciliation;

  // Kritikus hibák app-verziónkénti bontása az issues-ból
  const critByVersion = {};
  for (const k of CRIT_KEYS) {
    for (const iss of report?.checks?.[k]?.issues || []) {
      const v = iss?.appVersion || 'ismeretlen';
      critByVersion[v] = (critByVersion[v] || 0) + 1;
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <h2 style={{ margin: 0, color: '#e4e6eb', fontSize: 20 }}>
        🧪 Minőség {report?.alert && <span style={{ background: '#f43f5e', color: '#fff', fontSize: 11, padding: '2px 8px', borderRadius: 10, verticalAlign: 'middle', marginLeft: 8 }}>RIASZTÁS</span>}
      </h2>

      {/* Fejléc-sáv */}
      <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <input style={inp} type="date" value={date} onChange={(e) => { setDate(e.target.value); loadReport(e.target.value); }} />
        <button style={btn} onClick={runAudit} disabled={running || loading}>
          {running ? 'Audit fut…' : '▶️ Audit futtatása'}
        </button>
        <button style={{ ...btn, background: '#22c55e' }} onClick={exportExcel}
          disabled={!report || !exists || exporting || loading}
          title="A kiválasztott nap riportja Excelben: összefoglaló, ellenőrzések, találatok, 14 napos trend, kredit-egyeztetés, megoszlások">
          {exporting ? 'Készül…' : '📥 Excel jelentés'}
        </button>
        {loading && <span style={{ color: '#8b8fa3', fontSize: 12 }}>Betöltés…</span>}
        {!exists && !loading && (
          <span style={{ color: '#f59e0b', fontSize: 13 }}>
            Ehhez a naphoz még nincs jelentés — az „Audit futtatása" gombbal elkészítheted.
          </span>
        )}
      </div>

      {error && <div style={{ ...card, borderColor: '#f43f5e', color: '#f43f5e', fontSize: 13 }}>Hiba: {error}</div>}

      {/* Státusz-kártyák */}
      {report && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <Card label="Minták száma" value={report.sampleCount ?? 0} color="#00d4ff"
            sub={report.partialCount ? `ebből megszakadt: ${report.partialCount}` : null} />
          <Card label="Kritikus hibák" value={crit} color={crit > 0 ? '#f43f5e' : '#22c55e'}
            sub={checkNames(report, CRIT_KEYS)} />
          <Card label="Figyelmeztetések" value={warns} color={warns > 0 ? '#f59e0b' : '#22c55e'}
            sub={checkNames(report, WARN_KEYS)} />
          {rec?.ok === true && <Card label="Kredit-egyeztetés" value="✓" color="#22c55e"
            sub={rec.normalized
              ? `${rec.streamCharged ?? 0} levonás · mind párosítva (${rec.normalized.repaired} kulcs normalizálva)`
              : `${rec.streamCharged ?? 0} levonás · mind párosítva`} />}
          {rec && rec.ok === false && <Card label="Kredit-egyeztetés" value={orphanTotal(rec)} color="#f43f5e"
            sub={orphanSummary(rec)} />}
          {(!rec || rec.error) && <Card label="Kredit-egyeztetés" value="—" color="#8b8fa3" sub={rec?.error ? String(rec.error).slice(0, 60) : 'nincs adat'} />}
        </div>
      )}

      {/* Kulcs-normalizálás jelzése — a korrekció ne legyen néma */}
      {rec?.normalized && (
        <div style={{ ...card, borderColor: rec.normalized.listCapped ? '#f59e0b' : '#2a2e3a', fontSize: 12, color: '#8b8fa3', lineHeight: 1.6 }}>
          <b style={{ color: '#e4e6eb' }}>Kulcs-normalizálás:</b> {rec.normalized.repaired} tétel párosult össze
          requestId (UUID) alapján. Az auditban a levonás-oldal <code style={{ color: '#00d4ff' }}>&lt;uid&gt;_&lt;requestId&gt;</code>,
          a tranzakció-oldal <code style={{ color: '#00d4ff' }}>&lt;requestId&gt;</code> kulcsot használ, ezért pár nélkülinek
          látszottak. A dashboard a megjelenítés előtt egységesíti a kulcsokat.
          {rec.normalized.listCapped && (
            <div style={{ color: '#f59e0b', marginTop: 6 }}>
              ⚠️ Az audit a listákat 20 elemnél elvágja, így a korrekció csonkolt adaton dolgozott
              (eredetileg {rec.normalized.originalChargedWithoutTx} + {rec.normalized.originalTxWithoutCharged} tétel).
              A teljes körű javítás a <code>quality_audit.js</code>-ben van — utána az audit újrafuttatásával lesz végleges.
            </div>
          )}
        </div>
      )}

      {/* Kredit-egyeztetés részletei — mi az, ami ténylegesen nem párosult */}
      {rec && rec.ok === false && (
        <div style={{ ...card, borderColor: '#f43f5e' }}>
          <div style={title}>Kredit-egyeztetés — árva tételek</div>
          <div style={{ fontSize: 12, color: '#8b8fa3', marginBottom: 10, lineHeight: 1.6 }}>
            Az egyeztetés <b style={{ color: '#e4e6eb' }}>nem a darabszámokat</b> hasonlítja össze, hanem
            requestId szerint párosítja a levonásokat a tranzakció-logokkal. A napi darabszám ezért lehet
            azonos ({rec.streamCharged ?? '?'} levonás / {rec.streamTxLogged ?? '?'} log) úgy is, hogy közben
            marad párosítatlan tétel — a riasztást ezek okozzák, nem a számok eltérése.
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: 12, color: '#f43f5e', fontWeight: 700, marginBottom: 4 }}>
                Levonás tranzakció-log nélkül: {rec.chargedWithoutTx?.length || 0}
              </div>
              <div style={{ fontSize: 11, color: '#8b8fa3', marginBottom: 6 }}>
                Levontuk a kreditet, de nincs róla tranzakció-log — ez a „láthatatlan levonás” osztály.
              </div>
              {(rec.chargedWithoutTx || []).map((id) => (
                <div key={id} style={{ fontFamily: 'monospace', fontSize: 11, color: '#e4e6eb', padding: '2px 0' }}>{id}</div>
              ))}
              {!(rec.chargedWithoutTx || []).length && <div style={{ fontSize: 11, color: '#22c55e' }}>nincs ilyen ✓</div>}
            </div>

            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: 12, color: '#f59e0b', fontWeight: 700, marginBottom: 4 }}>
                Tranzakció-log levonás nélkül: {rec.txWithoutCharged?.length || 0}
              </div>
              <div style={{ fontSize: 11, color: '#8b8fa3', marginBottom: 6 }}>
                Van tranzakció-log, de nem találtunk hozzá levonást — dupla-log gyanú, vagy a kérés a
                lekérdezett ablakon (±15 perc rátartás) kívülre esett.
              </div>
              {(rec.txWithoutCharged || []).map((id) => (
                <div key={id} style={{ fontFamily: 'monospace', fontSize: 11, color: '#e4e6eb', padding: '2px 0' }}>{id}</div>
              ))}
              {!(rec.txWithoutCharged || []).length && <div style={{ fontSize: 11, color: '#22c55e' }}>nincs ilyen ✓</div>}
            </div>
          </div>
        </div>
      )}

      {/* Trend */}
      {trend.length > 1 && (
        <div style={card}>
          <div style={title}>14 napos trend — hibaarányok a napi mintaszámhoz képest (%)</div>
          <div style={{ width: '100%', height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={trend} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                <CartesianGrid stroke="#2a2e3a" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#8b8fa3" fontSize={11} />
                <YAxis stroke="#8b8fa3" fontSize={11} unit="%" />
                <Tooltip contentStyle={{ background: '#1a1d27', border: '1px solid #2a2e3a', borderRadius: 8, fontSize: 12 }} labelStyle={{ color: '#e4e6eb' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="kritikus" name="Kritikus hiba" stroke="#f43f5e" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="tropus" name="Trópusok" stroke="#a78bfa" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="hossz" name="Hossz-túllépés" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Ellenőrzés-részletek */}
      {report?.checks && (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{ ...title, padding: '14px 16px 6px' }}>Ellenőrzések</div>
          {orderedChecks(report.checks).map(([key, c]) => {
            const sev = c?.severity === 'error' ? 'error' : 'warn';
            const open = openCheck === key;
            const issues = c?.issues || [];
            const count = c?.count || 0;
            return (
              <div key={key} style={{ borderTop: '1px solid #2a2e3a' }}>
                <div onClick={() => setOpenCheck(open ? null : key)} title={CHECK_DESCRIPTIONS[key] || undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer' }}>
                  <span style={{ color: SEV_COLOR[sev] }}>●</span>
                  <span style={{ color: '#e4e6eb', fontSize: 14, fontWeight: 600 }}>{CHECK_LABELS[key] || key}</span>
                  <span style={{ color: (c?.count || 0) > 0 ? SEV_COLOR[sev] : '#22c55e', fontSize: 13, fontWeight: 700 }}>{c?.count || 0}</span>
                  <span style={{ marginLeft: 'auto', color: '#8b8fa3', fontSize: 12 }}>{open ? '▲' : '▼'}</span>
                </div>
                {open && (
                  <div style={{ padding: '0 16px 14px' }}>
                    {CHECK_DESCRIPTIONS[key] && (
                      <div style={{ color: '#8b8fa3', fontSize: 12, lineHeight: 1.5, marginBottom: 8 }}>{CHECK_DESCRIPTIONS[key]}</div>
                    )}
                    {/* Az audit az issue-listát levágja (legfeljebb 50), a count viszont a teljes szám. */}
                    {count > issues.length && issues.length > 0 && (
                      <div style={{ color: '#f59e0b', fontSize: 12, marginBottom: 8 }}>
                        Az első {issues.length} találat látszik, összesen {count}.
                      </div>
                    )}
                    {!issues.length ? (
                      <div style={{ color: '#8b8fa3', fontSize: 12 }}>
                        {count > 0 ? `${count} találat, de a riport nem tartalmaz példákat.` : 'Nincs találat ennél az ellenőrzésnél. 🎉'}
                      </div>
                    ) : (
                      (c.issues || []).map((iss, i) => (
                        <div key={i} style={{ background: '#0a0d14', border: '1px solid #2a2e3a', borderRadius: 8, padding: 10, marginBottom: 8 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: '#e4e6eb', marginBottom: 6 }}>
                            <span style={{ fontWeight: 600 }}>
                              {iss?.sampleId
                                ? <a href={`/narrations?id=${encodeURIComponent(iss.sampleId)}`} style={{ color: '#00d4ff' }}>{iss?.poi || '—'}</a>
                                : (iss?.poi || '—')}
                            </span>
                            <span style={{ color: '#8b8fa3' }}>{iss?.lang || '—'}</span>
                            <span style={{ color: '#8b8fa3' }}>v{iss?.appVersion || '?'}</span>
                          </div>
                          {iss?.detail && <div style={{ fontSize: 12, color: '#e4e6eb', marginBottom: 6 }}>{iss.detail}</div>}
                          {iss?.excerpt && (
                            <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#8b8fa3', whiteSpace: 'pre-wrap', borderLeft: `3px solid ${SEV_COLOR[sev]}`, paddingLeft: 8 }}>
                              {iss.excerpt}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Kisebb panelek */}
      {report && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ ...card, flex: 1, minWidth: 220 }}>
            <div style={title}>Trópusok</div>
            {Object.keys(report.tropes || {}).length === 0 ? (
              <div style={{ color: '#8b8fa3', fontSize: 12 }}>Nincs adat.</div>
            ) : (
              Object.entries(report.tropes || {}).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#e4e6eb', padding: '3px 0' }}>
                  <span>{TROPE_LABELS[k] || k}</span>
                  <span style={{ color: v > 0 ? '#f59e0b' : '#22c55e', fontWeight: 700 }}>{v}</span>
                </div>
              ))
            )}
            {report.duplication != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#e4e6eb', padding: '3px 0', borderTop: '1px solid #2a2e3a', marginTop: 6 }}>
                <span>Duplikáció</span>
                <span style={{ color: report.duplication > 0 ? '#f59e0b' : '#22c55e', fontWeight: 700 }}>{report.duplication}</span>
              </div>
            )}
            {report.lengthOver && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#e4e6eb', padding: '3px 0' }}>
                <span>Hossz-túllépés</span>
                <span style={{ color: (report.lengthOver.count || 0) > 0 ? '#f59e0b' : '#22c55e', fontWeight: 700 }}>
                  {report.lengthOver.count || 0}{report.lengthOver.worst ? ` (max ${report.lengthOver.worst})` : ''}
                </span>
              </div>
            )}
          </div>

          <div style={{ ...card, flex: 1.4, minWidth: 260 }}>
            <div style={title}>Gyakori n-gramok</div>
            {!(report.topNgrams || []).length ? (
              <div style={{ color: '#8b8fa3', fontSize: 12 }}>Nincs adat.</div>
            ) : (
              <div>
                <div style={{ color: '#f59e0b', fontSize: 11, marginBottom: 8 }}>Gyakori ismétlődő fordulatok — lehetséges új sablonok</div>
                {(report.topNgrams || []).map((n, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '3px 0' }}>
                    <span style={{ fontFamily: 'monospace', color: '#e4e6eb' }}>„{n?.ngram}"</span>
                    <span style={{ color: '#8b8fa3', flexShrink: 0 }}>{n?.count}×</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Régi riportban nincs byPromptPath → „Nincs adat.", nem dől el. */}
          <DistBars label="Prompt út" dist={report.byPromptPath} colors={PROMPT_PATH_COLORS}
            labelOf={(k) => PROMPT_PATH_LABELS[k] || k} order={['rich', 'short', 'unknown']}
            showZero emptyText="Nincs adat." style={{ minWidth: 220 }} />

          <div style={{ ...card, flex: 1, minWidth: 240 }}>
            <div style={title}>App-verziók</div>
            {Object.keys(report.byAppVersion || {}).length === 0 ? (
              <div style={{ color: '#8b8fa3', fontSize: 12 }}>Nincs adat.</div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8b8fa3', padding: '2px 0' }}>
                  <span>Verzió</span><span>minta / kritikus</span>
                </div>
                {Object.entries(report.byAppVersion || {}).sort((a, b) => b[1] - a[1]).map(([v, n]) => (
                  <div key={v} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#e4e6eb', padding: '3px 0' }}>
                    <span>{v}</span>
                    <span>
                      {n} / <span style={{ color: (critByVersion[v] || 0) > 0 ? '#f43f5e' : '#22c55e', fontWeight: 700 }}>{critByVersion[v] || 0}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* LLM szúrópróbák — csak ha van */}
      {Array.isArray(report?.llmSpotChecks) && report.llmSpotChecks.length > 0 && (
        <div style={card}>
          <div style={title}>LLM szúrópróbák</div>
          {report.llmSpotChecks.map((c, i) => (
            <div key={i} style={{ background: '#0a0d14', border: '1px solid #2a2e3a', borderRadius: 8, padding: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                {c?.sampleId
                  ? <a href={`/narrations?id=${encodeURIComponent(c.sampleId)}`} style={{ color: '#00d4ff' }}>{c?.poi || '—'}</a>
                  : <span style={{ color: '#e4e6eb' }}>{c?.poi || '—'}</span>}
                <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 400 }}>
                  <span style={{ color: c?.locationMismatch ? '#f43f5e' : '#22c55e' }}>helyszín {c?.locationMismatch ? '✗' : '✓'}</span>
                  {' · '}
                  <span style={{ color: c?.contradiction ? '#f43f5e' : '#22c55e' }}>konzisztencia {c?.contradiction ? '✗' : '✓'}</span>
                </span>
              </div>
              {c?.mismatchDetail && <div style={{ fontSize: 12, color: '#f43f5e' }}>{c.mismatchDetail}</div>}
              {c?.contradictionDetail && <div style={{ fontSize: 12, color: '#f59e0b' }}>{c.contradictionDetail}</div>}
            </div>
          ))}
        </div>
      )}

      {!report && !loading && exists && (
        <div style={{ ...card, color: '#8b8fa3', fontSize: 13 }}>Még nincs egyetlen minőségi jelentés sem a kollekcióban.</div>
      )}
    </div>
  );
}
